import { Address, scValToNative, xdr } from '@stellar/stellar-sdk';

import { FRACTION_DEN, MAX_DELEGATES, getIceDelegationConfig } from 'constants/ice-delegation';

import type Connection from 'services/soroban/connection/connection';
import { buildBatchCall, simulateBatch } from 'services/soroban/utils/connectionBatchSimulation';
import { publicKeyToScVal } from 'services/soroban/utils/scValHelpers';

import type {
    EnabledIceDelegationConfig,
    WeightedDelegation,
    WeightedDelegationsByProxy,
    WritableIceProxy,
} from 'types/ice-delegation';

export type DelegationSnapshot = {
    own: bigint;
    proxies: Record<WritableIceProxy, { current: WeightedDelegation[]; retained: bigint }>;
};

const fail = (): never => {
    throw new Error('Unexpected voting-power contract response. Please retry.');
};

export const parseProxyBalance = (value: xdr.ScVal | undefined): bigint => {
    if (value?.type !== 'scvI128') return fail();
    const amount = scValToNative(value) as bigint;
    if (amount < 0n) return fail();
    return amount;
};

export const parseWeightedDelegations = (
    value: xdr.ScVal | undefined,
    owner?: string,
): WeightedDelegation[] => {
    if (value?.type !== 'scvVec' || !value.vec) return fail();
    const rows = value.vec;
    if (rows.length > MAX_DELEGATES) return fail();
    const seen = new Set<string>();
    let total = 0;
    const result = rows.map(row => {
        if (row.type !== 'scvVec' || row.vec?.length !== 3) return fail();
        const [address, share, credit] = row.vec;
        if (address.type !== 'scvAddress' || share.type !== 'scvU32' || credit.type !== 'scvU64')
            return fail();
        const delegate = Address.fromScVal(address).toString();
        const fraction = share.u32;
        if (delegate === owner || seen.has(delegate) || fraction <= 0 || fraction > FRACTION_DEN)
            return fail();
        seen.add(delegate);
        total += fraction;
        return { delegate, fraction, matureCredit: credit.u64 };
    });
    if (total > FRACTION_DEN) return fail();
    return result;
};

export default class WeightedProxyContract {
    constructor(
        private readonly connection: Connection,
        private readonly configProvider: () => EnabledIceDelegationConfig = getIceDelegationConfig,
    ) {}

    private async simulate(owner: string, calls: xdr.ScVal[]): Promise<xdr.ScVal[]> {
        const values = await simulateBatch(
            this.connection,
            this.configProvider().batch,
            owner,
            calls,
            { unitSize: calls.length, maxCallsPerBatch: calls.length },
        );
        if (!Array.isArray(values) || values.length !== calls.length) return fail();
        return values;
    }

    // The upvoteICE and governICE edges in one simulation.
    async getDelegations(owner: string): Promise<WeightedDelegationsByProxy> {
        const { proxies } = this.configProvider();
        const args = [publicKeyToScVal(owner)];
        const [upvote, govern] = await this.simulate(owner, [
            buildBatchCall(proxies.upvote, 'delegations', args),
            buildBatchCall(proxies.govern, 'delegations', args),
        ]);
        return {
            upvote: parseWeightedDelegations(upvote, owner),
            govern: parseWeightedDelegations(govern, owner),
        };
    }

    // Own is the owner's power without incoming delegations. ICE cannot be delegated, so the
    // ICE proxy balance equals Own for upvoteICE and governICE. One simulation keeps every
    // value on the same ledger.
    async getDelegationSnapshot(owner: string): Promise<DelegationSnapshot> {
        const config = this.configProvider();
        const args = [publicKeyToScVal(owner)];
        const calls = [
            buildBatchCall(config.proxies.ice, 'balance', args),
            ...(['upvote', 'govern'] as const).flatMap(proxy => [
                buildBatchCall(config.proxies[proxy], 'delegations', args),
                buildBatchCall(config.proxies[proxy], 'get_retained_balance', args),
            ]),
        ];
        const values = await this.simulate(owner, calls);
        return {
            own: parseProxyBalance(values[0]),
            proxies: {
                upvote: {
                    current: parseWeightedDelegations(values[1], owner),
                    retained: parseProxyBalance(values[2]),
                },
                govern: {
                    current: parseWeightedDelegations(values[3], owner),
                    retained: parseProxyBalance(values[4]),
                },
            },
        };
    }
}
